/**
 * Dataset-prep hooks (Feature 1 — "Prepare" stage).
 *
 * - useStartDatasetPrep   POST /v1/loras/:id/dataset-prep (enqueues the action)
 * - useDatasetPrep        GET  /v1/loras/:id/dataset-prep (polls while running)
 * - useUpdateDatasetCaption PATCH /v1/loras/:id/dataset-captions/:assetId
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getDatasetPrep,
  startDatasetPrep,
  updateDatasetCaption,
} from "../../services/lorasApi";
import { loraQueryKeys } from "./queryKeys";
import type {
  LoraDatasetCaption,
  LoraDatasetPrep,
  LoraPrepStatusSlug,
  StartDatasetPrepResult,
} from "../../types/loras";

const NON_TERMINAL: ReadonlySet<LoraPrepStatusSlug> = new Set([
  "queued",
  "processing",
  "captioning",
  "packaging",
]);

/**
 * Poll the prep state while a run is in flight. `enabled` lets callers gate
 * the query until the wizard reaches the Prepare step.
 */
export function useDatasetPrep(loraId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: loraQueryKeys.datasetPrep(loraId ?? "none"),
    queryFn: () => getDatasetPrep(loraId as string),
    enabled: Boolean(loraId) && enabled,
    // Always refetch on mount to prevent stale data when switching between
    // passports. Without this, cached data from a previous passport may
    // incorrectly appear until a manual refetch.
    refetchOnMount: "always",
    // Poll every 2.5s while the prep run is non-terminal; stop once
    // prepared/failed (or there is no run yet — a single fetch shows the pool).
    refetchInterval: (query) => {
      const data = query.state.data as LoraDatasetPrep | undefined;
      const slug = data?.prepRun?.statusSlug;
      return slug && NON_TERMINAL.has(slug) ? 2500 : false;
    },
  });
}

export function useStartDatasetPrep() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (loraId: string) => startDatasetPrep(loraId),
    onSuccess: (res: StartDatasetPrepResult) => {
      // Refetch the prep state immediately + the LoRA (trigger word now set).
      queryClient.invalidateQueries({
        queryKey: loraQueryKeys.datasetPrep(res.loraId),
      });
      queryClient.invalidateQueries({
        queryKey: loraQueryKeys.detail(res.loraId),
      });
    },
  });
}

export function useUpdateDatasetCaption(loraId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, caption }: { assetId: string; caption: string }) =>
      updateDatasetCaption(loraId, assetId, caption),
    onSuccess: (updated: LoraDatasetCaption) => {
      // Patch the cached caption in place so the edit sticks without a refetch.
      queryClient.setQueryData<LoraDatasetPrep | undefined>(
        loraQueryKeys.datasetPrep(loraId),
        (prev) =>
          prev
            ? {
                ...prev,
                captions: prev.captions.map((c) =>
                  c.assetId === updated.assetId
                    ? { ...c, caption: updated.caption }
                    : c,
                ),
              }
            : prev,
      );
    },
  });
}
